import { t } from "./types";
import { z } from "zod";
import { DrivingLicenseClassEnum, isForCarOnly, isForCarWithTrailer, isForMotorcycle } from "./DrivingLicenseClass";
import { SpecialDrivingLessonProductTypeEnum, TheoryLessonProductTypeEnum } from "./ProductType";
import { RsvpEnum } from "./autovioCalendarEvents";

const PracticeKPIsSchema = t.object({
  finishedDrivingLessonsCount: t.preprocess(
    (val) => {
      // Prevent ZodError: invalid_enum_value when parsing old data ...
      if (val) {
        const { theoretischePruefung, ...rest } = val as any; // oxlint-disable-line no-unused-vars
        if (!rest.simulator) {
          rest.simulator = 0;
        }
        return rest;
      }
      return val;
    },
    t.record(
      t.enum([
        "normal",
        "ueberlandfahrt",
        "autobahnfahrt",
        "nachtfahrt",
        "simulator",
        "praktischePruefung",
        "schaltkompetenz",
      ]),
      t.number().nonnegative(),
    ),
  ),
  finishedManualGearLessonsCount: t.optional(t.number().nonnegative()),
  mostRecentDrivingLessonDate: t.optional(t.dateTime()),
  latestPlannedDrivingLessonDate: t.dateTime().optional(),
  practicalExams: t.optional(
    t.record(
      t.object({
        date: t.dateTime(),
        rsvp: t.optional(RsvpEnum),
        result: t.optional(t.enum(["passed", "failed"])),
      }),
    ),
  ),
  finishedObservationDrive: t.optional(t.boolean()),
});

export const BookedTrainingSchema = t
  .object({
    id: t.id(),
    asfCourseId: t.optional(t.id()),
    bundleId: t.optional(t.id()),
    bundleName: t.string().nonempty(),
    createdAt: t.optional(t.dateTime()),
    updatedAt: t.optional(t.dateTime()),
    updatedBy: t.optional(t.id()),
    drivingLicenseClass: DrivingLicenseClassEnum,
    guaranteedDrivingLessonsPerWeek: t.number().int().nonnegative(),
    quoteId: t.id(),
    trainingId: t.id(),
    drivingSchoolId: t.id(),
    instructorId: t.id().nullish(),
    prices: t.record(/* label */ t.string().nonempty(), /* cents */ t.number().nonnegative().int()),
    kpis: t.optional(PracticeKPIsSchema),
    drivingSchoolChange: t.optional(t.boolean()),
    drivingLicenseClassAdvancement: t.optional(t.boolean()),
    compulsoryTheoryLessons: z
      .optional(z.record(TheoryLessonProductTypeEnum, z.number().int().min(1)))
      .transform((val) => val || {}),
    compulsorySpecialDrivingLessons: z.optional(z.record(SpecialDrivingLessonProductTypeEnum, z.number().int().min(1))),
    includesSimulatorDrivingLessons: t.optional(t.boolean()),
    includesTheoryExam: t.optional(t.boolean()),
    includesPracticalExam: t.optional(t.boolean()),
    hasPassedTheoryExam: t.optional(t.boolean()),
    hasPassedPracticalExam: t.optional(t.boolean()),
    isFinished: t.optional(t.boolean()),
    isCancelled: z.boolean().nullish(),
    cancelledAt: t.dateTime().nullish(),
    cancelReason: z.string().min(1).nullish(),
    cancelledBy: z.string().min(1).nullish(),
  })
  .transform((val) => {
    if (!val.hasPassedPracticalExam) {
      if (Object.values(val.kpis?.practicalExams ?? {}).some((it) => it.result === "passed")) {
        val.hasPassedPracticalExam = true;
        val.isFinished = true;
      }
    }
    return val;
  });

export type BookedTraining = z.infer<typeof BookedTrainingSchema> & {
  /** not stored in Firestore, but derived from compulsoryTheoryLessons. */
  hasCompulsoryTheoryLessons: boolean;
  /** not stored in Firestore, either the driving license class ("e.g. "B", "B197", "A1", etc) or "ASF-Kurs" */
  shortLabel: string;
  /** not stored in Firestore, but derived from `bundleName`. */
  isForDrivingLicense?: true;
  /** not stored in Firestore, but derived from `asfCourseId` or `bundleName`. */
  isASF?: boolean;
  /** not stored in Firestore, used to sort the booked trainings of a student. */
  _sortOrder: number;
};

export function bookedTrainingsFromFirestore(bookedTrainingsData: any): Array<BookedTraining> {
  return Object.values(bookedTrainingsData ?? {})
    .map((data) => {
      const it = BookedTrainingSchema.parse(data);
      return {
        ...it,
        hasCompulsoryTheoryLessons: Object.keys(it.compulsoryTheoryLessons ?? {}).length > 0,
        isFinished: it.isFinished || Object.values(it.kpis?.practicalExams ?? {}).some((it) => it.result === "passed"),
        ...(() => {
          const isASF = !!it.asfCourseId || it.bundleName.includes("ASF");
          if (isASF) {
            return { shortLabel: "ASF-Kurs", isASF, _sortOrder: 9 };
          } else if (it.bundleName.includes("Fahrangst")) {
            return { shortLabel: "Fahrangst-Training", _sortOrder: 9 };
          } else if (it.bundleName.includes("MPU")) {
            return { shortLabel: "MPU", _sortOrder: 9 };
          } else {
            return {
              isForDrivingLicense: true,
              shortLabel: it.drivingLicenseClass,
              _sortOrder: isForCarOnly(it) ? 1 : isForMotorcycle(it) ? 2 : isForCarWithTrailer(it) ? 3 : 4,
            };
          }
        })(),
      } satisfies BookedTraining;
    })
    .sort((a, b) => a._sortOrder - b._sortOrder || a.bundleName.localeCompare(b.bundleName));
}
